import { Injectable } from '@angular/core';
import { AuthService } from 'src/app/modules/auth/services/auth.service';

interface PermissionRoute {
  path: string;
  permission: string;
  adminOnly?: boolean;
}

const PERMISSION_ROUTES: PermissionRoute[] = [
  { path: '/dashboard', permission: 'dashboard.view' },
  { path: '/apps/customer-booking', permission: 'customer-bookings.view' },
  { path: '/apps/cases', permission: 'cases.view' },
  { path: '/apps/calendar', permission: 'calendar.view' },
  { path: '/apps/customers', permission: 'customers.view' },
  { path: '/apps/accounting', permission: 'accounting.view' },
  { path: '/apps/worklist', permission: 'cases.view' },
  { path: '/apps/performance', permission: 'performance.view' },
  { path: '/apps/users', permission: 'users.view' },
];

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly permissionClaim = 'permission';

  constructor(private readonly authService: AuthService) {}

  has(permission: string): boolean {
    return this.isAdmin() || this.permissions.includes(permission);
  }

  hasAny(permissions: string[]): boolean {
    return permissions.some((permission) => this.has(permission));
  }

  hasModule(moduleCode: string): boolean {
    return this.has(`module.${moduleCode}.enabled`);
  }

  hasFeature(moduleCode: string, featureCode: string): boolean {
    return this.has(`feature.${moduleCode}.${featureCode}.enabled`);
  }

  canView(permission: string): boolean {
    return this.has(permission);
  }

  canAction(permission: string): boolean {
    return this.has(permission);
  }

  isAdmin(): boolean {
    return this.authService.hasAnyRole(['Admin']);
  }

  getLandingRoute(): string {
    const route = PERMISSION_ROUTES.find((candidate) =>
      this.has(candidate.permission) && (!candidate.adminOnly || this.isAdmin()));

    return route?.path ?? '/notifications';
  }

  private get permissions(): string[] {
    const token = this.authService.getAccessToken();
    if (!token) return [];

    try {
      const payload = JSON.parse(this.decodeBase64Url(token.split('.')[1] || '')) as Record<string, unknown>;
      const claim = payload[this.permissionClaim];
      return Array.isArray(claim)
        ? claim.filter((value): value is string => typeof value === 'string')
        : typeof claim === 'string' ? [claim] : [];
    } catch {
      return [];
    }
  }

  private decodeBase64Url(value: string): string {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = normalized.length % 4;
    return atob(padding ? normalized.padEnd(normalized.length + 4 - padding, '=') : normalized);
  }
}
